import React, { useCallback, useEffect, useRef, useState } from 'react';
import FoldersItem from '../../../../components/FolderItem/FolderItem';
import { useUserContext } from '../../../../context/UserContext';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import { useLibraryContext } from '../../../../context/LibraryContext';
import CmsAPI from '../../../../../common/apis/CmsAPI';
import Toast from '../../../../../common/utils/Toast';
import { useTranslation } from 'react-i18next';
import DeleteSharedFolderModal from '../../../../components/DeleteSharedFolderModal/DeleteSharedFolderModal';
import { FOLDER_USER_ROLE } from '../../../../constants/UserRoles';
import { ToastNotification } from '../../../../components/Toast/Toast';
import Col from '../../../../components/GridSystem/Col';
import Row from '../../../../components/GridSystem/Row';
import EmptyFolders from '../../../../components/EmptyFolders/EmptyFolders';
import RecordNotFound from '../../../../components/_empty_state/RecordNotFound';
import styles from './FoldersList.module.scss';
import { clearEmojis } from '../../../../utils/stringHelper';
import { ESCAPE_KEY_CODE } from '../../../../constants/common';

const folderToDeleteInit = {
  id: '',
  folderIndex: -1,
  name,
  owner: {},
  users: [],
  isFolderEditable: false,
};

const deleteSharedFolderModalInit = {
  isShow: false,
  isOwner: false,
  isMultipleDeletion: false,
};

const FoldersList = ({
  folders,
  isTeam = false,
  loadFolders,
  loadVideos,
  listMode,
  searchInput,
  shareFolder,
  updateFolderList,
  archive = false,
  folderQuery,
  folderLoading,
}) => {
  const { user } = useUserContext();
  const { t } = useTranslation();

  const {
    selectedCard,
    toggleSelectedFolder,
    selectedFolder,
    toggleUnselectFolders,
    isDeleteFolders,
    toggleDeleteFolders,
    isArchiveFolders,
    toggleArchiveFolders,
    isUnarchiveFolders,
    toggleUnarchiveFolders,
    isRenameFolder,
    toggleRenameFolder,
  } = useLibraryContext();

  const [showMenu, setShowMenu] = useState({});
  const [renameFolderData, setRenameFolderData] = useState({});
  const [folderToDelete, setFolderToDelete] = useState(folderToDeleteInit);
  const [confirmingDeleting, setConfirmingDeleting] = useState(false);
  const [folderNameError, setFolderNameError] = useState(false);
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [deleteSharedFolderModalState, setDeleteSharedFolderModalState] = useState(deleteSharedFolderModalInit);

  const [squeezeCard, setSqueezeCard] = useState(false);

  useEffect(() => {
    setSqueezeCard(Object.keys(selectedCard).length + Object.keys(selectedFolder).length);
  }, [selectedCard, selectedFolder]);

  const menuRef = useRef(null);
  useOutsideClick(menuRef, () => {
    if (showMenu && Object.keys(showMenu).length) {
      setShowMenu({});
    }
  });

  const confirmDeleteSharedFolder = () => {
    setDeleteSharedFolderModalState({
      isShow: false,
      isOwner: false,
      isEditorWithRights: false,
      isMultipleDeletion: false,
    });

    // wait 100 ms for the modal to close
    setTimeout(() => setConfirmingDeleting(true), 100);
  };

  const confirmDeleteSharedFolders = () => {
    setDeleteSharedFolderModalState({
      isShow: false,
      isOwner: false,
      isEditorWithRights: false,
      isMultipleDeletion: false,
    });

    // wait 100 ms for the modal to close
    setTimeout(deleteFolders, 100);
  };

  const closeDeleteSharedModal = () => {
    if (isDeleteFolders) {
      toggleDeleteFolders();
    }
    setDeleteSharedFolderModalState({
      isShow: false,
      isOwner: false,
      isEditorWithRights: false,
      isMultipleDeletion: false,
    });
  };

  const showDeleteConfirmation = useCallback((id, folderIndex, name, owner, users, isFolderEditable) => {
    if (id !== '') {
      setFolderToDelete({
        id,
        folderIndex,
        name,
        owner,
        users,
        isFolderEditable,
      });
    }

    const isSharedFolder = users?.length;
    const isFolderOwner = owner?.username === user.username;
    const isEditor = users?.find(
      ({ username, folderRole }) => username === user.username && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE
    );

    if (isSharedFolder) {
      setDeleteSharedFolderModalState({
        isShow: true,
        isOwner: isFolderOwner,
        isEditorWithRights: isEditor && isFolderEditable,
        isMultipleDeletion: false,
      });
      return;
    }

    setConfirmingDeleting(true);
  }, []);

  useEffect(() => {
    if (confirmingDeleting) {
      deleteFolderForever();
    }
  }, [confirmingDeleting]);

  useEffect(() => {
    if (isRenameFolder) {
      const uuid = Object.keys(selectedFolder)[0];
      renameFolder(uuid);
      toggleRenameFolder();
      toggleUnselectFolders();
    }
  }, [isRenameFolder]);

  useEffect(() => {
    const isSharedFolders = Object.values(selectedFolder).find(folderItem => folderItem?.users?.length);

    const isFoldersOwner =
      !!Object.values(selectedFolder).length &&
      Object.values(selectedFolder).every(({ owner }) => {
        return owner?.username === user.username;
      });

    const isFoldersEditor =
      !!Object.values(selectedFolder).length &&
      Object.values(selectedFolder).every(({ users }) => {
        return users?.find(({ username, folderRole }) => username === user.username && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE);
      });

    const isFoldersEditable =
      !!Object.values(selectedFolder).length &&
      Object.values(selectedFolder).every(({ editingEnabled }) => {
        return editingEnabled;
      });

    if (isDeleteFolders) {
      if (isSharedFolders) {
        setDeleteSharedFolderModalState({
          isShow: true,
          isOwner: isFoldersOwner,
          isEditorWithRights: isFoldersEditor && isFoldersEditable,
          isMultipleDeletion: true,
        });
        return;
      }

      deleteFolders();
    }
  }, [isDeleteFolders]);

  const deleteFolders = async () => {
    await deleteFolderForever();
    toggleDeleteFolders();
  };

  const deleteFolderForever = useCallback(async () => {
    const confirmAction = confirm(
      `Are you sure you want to permanently delete ${
        folderToDelete.name ? `"${folderToDelete.name}" and all it\’s` : 'the selected folders and all their'
      } contents? This action cannot be undone.`
    );
    if (confirmAction) {
      try {
        const { id, folderIndex, name } = folderToDelete;
        if (folderIndex === -1) {
          const uuids = Object.values(selectedFolder);
          Promise.all(
            uuids.map(({ id }) => {
              return CmsAPI.deleteFolder(id);
            })
          ).then(() => {
            ToastNotification({
              message: `${t('general.toast.delete_successfully_folders')}`,
              iconName: 'success',
              isMobileView: isMobileView,
            });
            setConfirmingDeleting(false);
            setFolderToDelete(folderToDeleteInit);
            loadFolders();
            loadVideos();
            toggleUnselectFolders();
            updateFolderList();
          });
        } else {
          if (Object.keys(selectedFolder).find(item => item === id)) toggleSelectedFolder({ id });
          await CmsAPI.deleteFolder(id);
          ToastNotification({
            message: `${t('general.toast.delete_successfully')} ${name}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadFolders();
          loadVideos();
          toggleUnselectFolders();
          setConfirmingDeleting(false);
          setFolderToDelete(folderToDeleteInit);
          updateFolderList();
        }
      } catch (e) {
        Toast.error(e);
      }
    } else {
      setConfirmingDeleting(false);
      setFolderToDelete(folderToDeleteInit);
    }
  }, [folderToDelete, selectedFolder]);

  const handleDotsClick = id => {
    setShowMenu({
      uuid: id,
      flag: true,
    });
  };

  const archiveFolders = useCallback(
    async (uuid, name) => {
      try {
        if (!uuid && Object.keys(selectedFolder).length) {
          const uuids = Object.keys(selectedFolder);
          Promise.all(
            uuids.map(uuid => {
              return CmsAPI.updateFolder(uuid, { archived: 1 });
            })
          ).then(() => {
            loadFolders();
            toggleUnselectFolders();
            updateFolderList();
            ToastNotification({
              message: `${t('general.toast.archive_successfully_folders')}`,
              iconName: 'archive',
              isMobileView: isMobileView,
            });
          });
        } else if (uuid) {
          console.log('else if(uuid)');
          if (Object.keys(selectedFolder).find(item => item === uuid)) toggleSelectedFolder({ id: uuid });
          await CmsAPI.updateFolder(uuid, { archived: 1 });
          await loadFolders();
          ToastNotification({
            message: `${t('general.toast.archive_successfully')} '${name}'`,
            iconName: 'archive',
            isMobileView: isMobileView,
          });
        }
      } catch (e) {
        Toast.error('Unable to archive this folder.', e);
      }
    },
    [selectedFolder]
  );

  const unarchiveFolders = useCallback(
    async uuid => {
      try {
        if (!uuid && Object.keys(selectedFolder).length) {
          const uuids = Object.keys(selectedFolder);
          Promise.all(
            uuids.map(uuid => {
              return CmsAPI.updateFolder(uuid, { archived: 0 });
            })
          ).then(() => {
            loadFolders();
            toggleUnselectFolders();
            ToastNotification({
              message: `${t('general.toast.unarchive_successfully_folders')}`,
              iconName: 'unarchive',
              isMobileView: isMobileView,
            });
          });
        } else if (uuid) {
          if (Object.keys(selectedFolder).find(item => item === uuid)) {
            toggleSelectedFolder({ id: uuid });
          }
          const folderName = folders.find(item => item.id === uuid)?.name;
          await CmsAPI.updateFolder(uuid, { archived: 0 });
          await loadFolders();
          ToastNotification({
            message: `${t('general.toast.unarchive_successfully')} '${folderName}'`,
            iconName: 'unarchive',
            isMobileView: isMobileView,
          });
        }
      } catch (e) {
        Toast.error('Unable to unarchive this folder.', e);
      }
    },
    [selectedFolder, folders]
  );

  useEffect(() => {
    const archive = async () => {
      await archiveFolders();
      toggleArchiveFolders();
    };
    if (isArchiveFolders) {
      archive();
    }
  }, [isArchiveFolders]);

  useEffect(() => {
    console.log('useEffect isUnarchiveFolders', isUnarchiveFolders);
    const unarchive = async () => {
      await unarchiveFolders();
      toggleUnarchiveFolders();
    };
    if (isUnarchiveFolders) {
      unarchive();
    }
  }, [isUnarchiveFolders]);

  const renameFolder = useCallback(
    async uuid => {
      document.addEventListener('keydown', handleCancelFolderRename);
      setRenameFolderData({
        [uuid]: {
          name: folders.find(({ id }) => id === uuid).name,
        },
      });
    },
    [setRenameFolderData, folders]
  );

  const onRenameInputChange = (e, id) => {
    const newName = clearEmojis(e.target.value.substring(0, 100));
    setRenameFolderData({
      [id]: {
        name: newName,
      },
    });
    if (folders.find(item => item.name === newName && item.id !== id) || newName.trim() === '') {
      setFolderNameError(true);
    } else {
      folderNameError && setFolderNameError(false);
    }
  };

  const handleCancelFolderRename = e => {
    if (e.keyCode === ESCAPE_KEY_CODE) {
      setRenameFolderData({});
    }
  };

  useEffect(() => {
    if (Object.keys(renameFolderData).length === 0) {
      document.removeEventListener('keydown', handleCancelFolderRename);
    }
  }, [renameFolderData]);

  const renameFolderAction = useCallback(
    id => {
      if (!folderNameError && !(folders.find(item => item.id === id)?.name === renameFolderData[id].name.trim())) {
        return CmsAPI.updateFolder(id, { name: renameFolderData[id].name.trim() }).then(() => {
          setRenameFolderData({});
          loadFolders();
        });
      } else {
        setRenameFolderData({});
      }
    },
    [renameFolderData, folders, loadFolders, setRenameFolderData]
  );

  const handleCardClick = (event, folderIndex) => {
    const parentElement = event.target.parentElement.parentElement;
    if (
      parentElement.tagName !== 'BUTTON' &&
      parentElement.tagName !== 'LI' &&
      parentElement.tagName !== 'UL' &&
      event.target.tagName !== 'LI' &&
      event.target.tagName !== 'UL'
    ) {
      console.log('toggleSelectedFolder', folderIndex);
      const filteredFolders = folders.filter(({ users }) => archive || (isTeam ? users?.length : !users?.length));
      toggleSelectedFolder(filteredFolders[folderIndex]);
    }
  };

  return (
    <>
      {folderLoading && <h2>{t('general.loading')}</h2>}
      {!folderLoading && (
        <Col xs={12} className={styles.videoLibrary}>
          <Row className={styles.row}>
            {folders
              .filter(({ users }) => archive || (isTeam ? users?.length : !users?.length))
              .map((folder, index) => (
                <FoldersItem
                  key={folder.id}
                  listMode={listMode}
                  folder={folder}
                  folderIndex={index}
                  handleCardClick={handleCardClick}
                  handleDotsClick={handleDotsClick}
                  showMenu={showMenu}
                  menuRef={menuRef}
                  toggleShowMenu={setShowMenu}
                  shareFolder={archive ? undefined : shareFolder}
                  renameFolder={archive ? undefined : renameFolder}
                  archiveFolder={archive || isTeam ? undefined : archiveFolders}
                  deleteFolder={showDeleteConfirmation}
                  unarchiveFolder={archive ? unarchiveFolders : undefined}
                  renameFolderData={renameFolderData}
                  onRenameInputChange={onRenameInputChange}
                  renameFolderAction={renameFolderAction}
                  folderNameError={folderNameError}
                  searchInput={searchInput}
                  isMobileView={isMobileView}
                  squeezeCard={squeezeCard}
                />
              ))}
            <DeleteSharedFolderModal
              state={deleteSharedFolderModalState}
              folderToDelete={folderToDelete}
              foldersToDelete={Object.values(selectedFolder)}
              confirmDeleteSharedFolder={confirmDeleteSharedFolder}
              confirmDeleteSharedFolders={confirmDeleteSharedFolders}
              onClose={closeDeleteSharedModal}
            />
          </Row>
        </Col>
      )}
      {folders?.filter(({ users }) => archive || (isTeam ? users?.length : !users?.length))?.length === 0 && (
        <>
          {folderQuery && !folderLoading && <RecordNotFound searchTerm={folderQuery} />}
          {!folderQuery && !folderLoading && <EmptyFolders archive={archive} type={isTeam ? 'team' : 'folders'} />}
        </>
      )}
    </>
  );
};

export default FoldersList;
